<template>
    <vx-expandable-card>
        <template v-slot:content>
            <div class="flex flex-col gap-4">
                <div>
                    <vx-label size="md">Street</vx-label>
                    <vx-input
                        v-model="form.street"
                        size="lg"
                        data-test="street-input"
                        :error="displayFirstError(validate.street.$errors)"
                    ></vx-input>
                </div>

                <div data-test="country-select">
                    <vx-label size="md">Country</vx-label>
                    <vx-selectable
                        v-model="form.country"
                        size="lg"
                        :options="countriesList"
                        :reduce="(option: AddressOption) => option.value"
                        :clearable="false"
                        :error="displayFirstError(validate.country.$errors)"
                    ></vx-selectable>
                </div>

                <div>
                    <vx-label size="md">City</vx-label>
                    <vx-input
                        v-model="form.city"
                        size="lg"
                        data-test="city-input"
                        :error="displayFirstError(validate.city.$errors)"
                    ></vx-input>
                </div>

                <div data-test="state-select">
                    <vx-label size="md">State</vx-label>
                    <vx-selectable
                        v-if="showStateDropdown"
                        v-model="form.state"
                        size="lg"
                        :options="stateOptions"
                        :reduce="(option: AddressOption) => option.value"
                        :clearable="false"
                        :error="displayFirstError(validate.state.$errors)"
                    ></vx-selectable>
                    <vx-input
                        v-else
                        v-model="form.state"
                        size="lg"
                        data-test="state-input"
                        :error="displayFirstError(validate.state.$errors)"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Zip</vx-label>
                    <vx-input
                        v-model="form.postal_code"
                        size="lg"
                        data-test="zip-input"
                        :error="displayFirstError(validate.postal_code.$errors)"
                    ></vx-input>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { VxExpandableCard, VxInput, VxLabel, VxSelectable } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { alphaNum, helpers, maxLength, minLength, numeric, required } from '@vuelidate/validators';
import { computed, toRef } from 'vue';
import { useAddressStateSelector } from '~/composables/useAddressStateSelector';
import { countries as countriesList, states as statesList } from '~/constants/countries';
import type { TcrBrandCreate } from '~/services/adminTenDlcRegistrationClient.types';
import { displayFirstError } from '~/utils/validation';

type AddressOption = { label: string; value: string };

const props = defineProps<{ externalResults: Record<string, string[]> }>();
const form = defineModel<TcrBrandCreate>({ required: true });

const { showStateDropdown } = useAddressStateSelector(form, {
    resetFields: ['city', 'state', 'postal_code'],
    emptyValue: '',
});

const stateOptions = computed<AddressOption[]>(() => statesList[form.value.country as keyof typeof statesList] ?? []);

const numericZip = helpers.withMessage('The ZIP code must only contain numbers', numeric);
const alphaNumZip = helpers.withMessage('The ZIP code must be alpha-numeric', alphaNum);
const zipLengths: Record<string, number> = { US: 5, CA: 6 };

// Mirrors AddressZipRule, within TCR's ten characters.
const validationRules = computed(() => {
    const zipLength = zipLengths[form.value.country];

    return {
        street: { required, maxLength: maxLength(255) },
        country: { required },
        city: { required, maxLength: maxLength(100) },
        state: { required, maxLength: maxLength(showStateDropdown.value ? 2 : 20) },
        postal_code: {
            required,
            format: 'US' === form.value.country ? numericZip : alphaNumZip,
            minLength: minLength(zipLength ?? 1),
            maxLength: maxLength(zipLength ?? 10),
        },
    };
});

const validate = useVuelidate(validationRules, form, { $externalResults: toRef(props, 'externalResults') });
</script>
